import React, { useCallback, useEffect, useState } from "react";
import { suppliersAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDate, formatDateTime } from "../utils/dates";
import Modal from "../components/Modal";
import EmptyState from "../components/EmptyState";

const field = "mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";

/**
 * Who the shop buys from, and what it owes each of them.
 *
 * Most owed first, because that is the question when a supplier walks in:
 * "what do I still owe you?". Paying is one tap from the list — the whole
 * balance is filled in, and changed when only part is paid.
 */
const Suppliers = () => {
  const { t } = useI18n();
  const { can, isOwner } = useMembership();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);

  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [adding, setAdding] = useState(null);
  const [paying, setPaying] = useState(null);
  const [history, setHistory] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await suppliersAPI.list();
      setSuppliers(data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || t("suppliers.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action, success) => {
    setBusy(true);
    setError("");
    try {
      await action();
      setNotice(success);
      await load();
      return true;
    } catch (err) {
      setError(err.response?.data?.message || t("suppliers.saveFailed"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addSupplier = async (event) => {
    event.preventDefault();
    const done = await run(() => suppliersAPI.create(adding), t("suppliers.added", { name: adding.name }));
    if (done) setAdding(null);
  };

  const pay = async (event) => {
    event.preventDefault();
    const { supplier, amount, source, note } = paying;
    const done = await run(
      () => suppliersAPI.pay(supplier.id, { amount: parseFloat(amount), source, note: note || undefined }),
      t("suppliers.paid", { name: supplier.name, amount: money(amount) }),
    );
    if (done) setPaying(null);
  };

  const openHistory = async (supplier) => {
    setHistory({ supplier, loading: true });
    try {
      const { data } = await suppliersAPI.history(supplier.id);
      setHistory({ ...data, loading: false });
    } catch (err) {
      setHistory({ supplier, loading: false, failed: true });
    }
  };

  const totalOwed = suppliers.reduce((sum, supplier) => sum + Math.max(supplier.balance, 0), 0);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{t("suppliers.title")}</h1>
          <p className="text-gray-600">{t("suppliers.subtitle")}</p>
        </div>
        <div className="flex items-center gap-4">
          {suppliers.length > 0 && (
            <div className="text-end">
              <p className="text-xs uppercase tracking-wide text-gray-500">{t("suppliers.totalOwed")}</p>
              <p className="text-2xl font-bold text-amber-700">{money(totalOwed)}</p>
            </div>
          )}
          {can("suppliers.edit") && (
            <button
              onClick={() => setAdding(adding ? null : { name: "", phone: "", notes: "" })}
              className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
            >
              {adding ? t("common.cancel") : t("suppliers.add")}
            </button>
          )}
        </div>
      </div>

      {error && !paying && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}
      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {adding && (
        <form onSubmit={addSupplier} className="bg-white rounded-xl shadow-lg p-6 grid gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">{t("suppliers.name")}</span>
            <input
              type="text"
              required
              value={adding.name}
              onChange={(e) => setAdding({ ...adding, name: e.target.value })}
              className={field}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">{t("suppliers.phone")}</span>
            <input
              type="tel"
              dir="ltr"
              value={adding.phone}
              onChange={(e) => setAdding({ ...adding, phone: e.target.value })}
              className={field}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">{t("suppliers.notes")}</span>
            <input
              type="text"
              value={adding.notes}
              onChange={(e) => setAdding({ ...adding, notes: e.target.value })}
              className={field}
            />
          </label>
          <div className="sm:col-span-3">
            <button
              type="submit"
              disabled={busy}
              className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
            >
              {busy ? t("common.saving") : t("suppliers.save")}
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
        {loading ? (
          <p className="text-gray-500 py-8 text-center">{t("common.loading")}</p>
        ) : suppliers.length === 0 ? (
          <EmptyState screen="suppliers" message={t("suppliers.none")} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {suppliers.map((supplier) => (
              <li key={supplier.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{supplier.name}</p>
                  <p className="text-sm text-gray-500">
                    {supplier.phone && <span dir="ltr">{supplier.phone} · </span>}
                    {supplier.last_delivery
                      ? t("suppliers.lastDelivery", { day: formatDate(supplier.last_delivery) })
                      : t("suppliers.noDelivery")}
                  </p>
                  {supplier.awaiting_prices > 0 && (
                    <p className="text-xs font-medium text-amber-700">
                      {t("suppliers.awaitingPrices", { count: supplier.awaiting_prices })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`font-semibold whitespace-nowrap ${
                      supplier.balance > 0 ? "text-amber-700" : "text-gray-500"
                    }`}
                  >
                    {supplier.balance > 0
                      ? t("suppliers.owed", { amount: money(supplier.balance) })
                      : supplier.balance < 0
                        ? t("suppliers.ahead", { amount: money(-supplier.balance) })
                        : t("suppliers.settled")}
                  </span>
                  <button
                    onClick={() => openHistory(supplier)}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold"
                  >
                    {t("suppliers.history")}
                  </button>
                  {can("suppliers.edit") && (
                    <button
                      onClick={() =>
                        setPaying({
                          supplier,
                          amount: supplier.balance > 0 ? String(supplier.balance) : "",
                          source: "outside",
                          note: "",
                        })
                      }
                      className="px-3 py-1.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold"
                    >
                      {t("suppliers.pay")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {paying && (
        <Modal
          title={t("suppliers.payTitle", { name: paying.supplier.name })}
          onClose={() => setPaying(null)}
          maxWidthClass="max-w-md"
        >
          <form onSubmit={pay} className="space-y-4">
            <p className="text-sm text-gray-600">
              {t("suppliers.currentlyOwed", { amount: money(Math.max(paying.supplier.balance, 0)) })}
            </p>
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>
            )}
            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("suppliers.amount", { currency })}</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                required
                dir="ltr"
                value={paying.amount}
                onChange={(e) => setPaying({ ...paying, amount: e.target.value })}
                className={field}
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("finance.deliveries.paidFrom")}</span>
              <select
                value={paying.source}
                onChange={(e) => setPaying({ ...paying, source: e.target.value })}
                className={`${field} bg-white`}
              >
                <option value="outside">{t("finance.deliveries.fromOutside")}</option>
                {can("power.manage_cash") && (
                  <option value="drawer">{t("finance.deliveries.fromDrawer")}</option>
                )}
                {isOwner && (
                  <option value="safe">{t("finance.deliveries.fromSafe")}</option>
                )}
              </select>
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-700">{t("suppliers.notes")}</span>
              <input
                type="text"
                value={paying.note}
                onChange={(e) => setPaying({ ...paying, note: e.target.value })}
                className={field}
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              className="w-full px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
            >
              {busy ? t("common.saving") : t("suppliers.recordPayment")}
            </button>
          </form>
        </Modal>
      )}

      {history && (
        <Modal
          title={history.supplier.name}
          onClose={() => setHistory(null)}
          maxWidthClass="max-w-2xl"
        >
          {history.loading ? (
            <p className="text-gray-500">{t("common.loading")}</p>
          ) : history.failed ? (
            <p className="text-red-700">{t("suppliers.loadFailed")}</p>
          ) : history.timeline.length === 0 ? (
            <p className="text-gray-500">{t("suppliers.nothingYet")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {[...history.timeline].reverse().map((event) => (
                <li key={`${event.type}-${(event.delivery || event.payment).id}`} className="py-3 flex gap-3">
                  <span className="text-xl" aria-hidden="true">
                    {event.type === "delivery" ? "🚚" : "💸"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">
                      {event.type === "delivery"
                        ? t("suppliers.deliveryOf", { amount: money(event.amount) })
                        : t(
                            event.payment.source === "drawer"
                              ? "suppliers.paymentFromDrawer"
                              : event.payment.source === "safe"
                                ? "suppliers.paymentFromSafe"
                                : "suppliers.payment",
                            { amount: money(event.amount) },
                          )}
                    </p>
                    {event.type === "delivery" && (
                      <p className="text-sm text-gray-600">
                        {event.delivery.items
                          .map((item) => `${item.quantity}× ${item.product_name}`)
                          .join(", ")}
                        {event.delivery.status === "awaiting_prices" &&
                          ` · ${t("finance.deliveries.awaiting")}`}
                      </p>
                    )}
                    {event.payment?.note && <p className="text-sm text-gray-600">{event.payment.note}</p>}
                    <p className="text-xs text-gray-400">
                      {formatDateTime(event.at)}
                      {event.payment?.created_by_name && ` · ${event.payment.created_by_name}`}
                    </p>
                  </div>
                  <span className="shrink-0 text-sm text-gray-500 whitespace-nowrap">
                    {t("suppliers.owedAfter", { amount: money(event.owed_after) })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </div>
  );
};

export default Suppliers;
